import type { ApiProtocol, ModelHosting } from "@/types";
import {
  API_PROTOCOL_OPTIONS,
  endpointSuffixWarning,
  exampleEndpointUrl,
  HOSTING_OPTIONS,
} from "./modelEndpointConfig";

const SELECT_CLASS =
  "w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50";
const INPUT_CLASS = `${SELECT_CLASS} placeholder:text-content-quaternary`;

export interface EndpointProtocolFieldsProps {
  idPrefix: string;
  hosting: ModelHosting;
  apiProtocol: ApiProtocol;
  endpointUrl: string;
  hasApiKey?: boolean;
  apiKeyValue: string;
  clearApiKey?: boolean;
  onHostingChange: (hosting: ModelHosting) => void;
  onApiProtocolChange: (protocol: ApiProtocol) => void;
  onEndpointUrlChange: (url: string) => void;
  onApiKeyValueChange: (value: string) => void;
  onClearApiKeyChange?: (clear: boolean) => void;
}

/**
 * The unified "Endpoint + API Protocol" fields shared by the add/configure
 * model modals. Selecting the "custom" hosting progressively discloses the
 * endpoint URL + API key inputs (the former "Advanced: Endpoint Override"),
 * with protocol-aware, warn-only hints about the expected URL suffix.
 */
export function EndpointProtocolFields({
  idPrefix,
  hosting,
  apiProtocol,
  endpointUrl,
  hasApiKey,
  apiKeyValue,
  clearApiKey,
  onHostingChange,
  onApiProtocolChange,
  onEndpointUrlChange,
  onApiKeyValueChange,
  onClearApiKeyChange,
}: Readonly<EndpointProtocolFieldsProps>) {
  const isCustom = hosting === "custom";
  const suffixWarning = isCustom ? endpointSuffixWarning(endpointUrl, apiProtocol) : null;
  const hostingDescription = HOSTING_OPTIONS.find((h) => h.value === hosting)?.description;
  const protocolDescription = API_PROTOCOL_OPTIONS.find((p) => p.value === apiProtocol)?.description;

  return (
    <div className="space-y-4">
      {/* Endpoint (hosting) */}
      <div>
        <label htmlFor={`${idPrefix}-hosting`} className="block text-sm font-medium text-content-secondary mb-1.5">
          Endpoint
        </label>
        <select
          id={`${idPrefix}-hosting`}
          value={hosting}
          onChange={(e) => onHostingChange(e.target.value as ModelHosting)}
          className={SELECT_CLASS}
        >
          {HOSTING_OPTIONS.map((h) => (
            <option key={h.value} value={h.value}>
              {h.label}
            </option>
          ))}
        </select>
        {hostingDescription && <p className="text-xs text-content-quaternary mt-1">{hostingDescription}</p>}
      </div>

      {/* API Protocol */}
      <div>
        <label htmlFor={`${idPrefix}-api-protocol`} className="block text-sm font-medium text-content-secondary mb-1.5">
          API Protocol
        </label>
        <select
          id={`${idPrefix}-api-protocol`}
          value={apiProtocol}
          onChange={(e) => onApiProtocolChange(e.target.value as ApiProtocol)}
          className={SELECT_CLASS}
        >
          {API_PROTOCOL_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        {protocolDescription && <p className="text-xs text-content-quaternary mt-1">{protocolDescription}</p>}
      </div>

      {/* Custom endpoint fields — progressively disclosed */}
      {isCustom && (
        <div className="p-4 space-y-3 border border-border-secondary rounded-lg bg-surface-secondary/40">
          <p className="text-xs text-content-quaternary">
            Enter the full endpoint URL and API key. The URL is used verbatim (no suffix is added).
          </p>
          <div>
            <label
              htmlFor={`${idPrefix}-endpoint-url`}
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Endpoint URL
            </label>
            <input
              id={`${idPrefix}-endpoint-url`}
              type="text"
              value={endpointUrl}
              onChange={(e) => onEndpointUrlChange(e.target.value)}
              placeholder={exampleEndpointUrl(apiProtocol)}
              className={`${INPUT_CLASS} font-mono text-sm`}
            />
            {suffixWarning ? (
              <p className="text-xs text-amber-500 mt-1">{suffixWarning}</p>
            ) : (
              <p className="text-xs text-content-quaternary mt-1">e.g. {exampleEndpointUrl(apiProtocol)}</p>
            )}
          </div>
          <div>
            <label htmlFor={`${idPrefix}-api-key`} className="block text-sm font-medium text-content-secondary mb-1.5">
              API Key {hasApiKey && !clearApiKey && <span className="text-xs text-green-500 ml-1">(stored)</span>}
            </label>
            <input
              id={`${idPrefix}-api-key`}
              type="password"
              value={apiKeyValue}
              onChange={(e) => {
                onApiKeyValueChange(e.target.value);
                if (e.target.value && clearApiKey) onClearApiKeyChange?.(false);
              }}
              placeholder={
                hasApiKey ? "Enter new key to replace, or leave blank to keep" : "Paste API key (encrypted at rest)"
              }
              className={`${INPUT_CLASS} font-mono text-sm`}
            />
            {hasApiKey && onClearApiKeyChange && (
              <label className="flex items-center gap-2 mt-2 text-xs text-content-quaternary cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearApiKey}
                  onChange={(e) => onClearApiKeyChange(e.target.checked)}
                  className="w-3.5 h-3.5 rounded-sm border-border-secondary"
                />
                <span>Remove stored API key (revert to env var or default)</span>
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
